import { useEffect, useState } from 'react';

/**
 * Tracks `document.visibilityState`. Returns `false` while the tab is hidden
 * (user switched to another tab/window, or the page was put in the background).
 *
 * Session hooks use this to release their lease while hidden — closing the
 * relay WebSocket + stopping the keepalive `request` so a backgrounded tab does
 * not (a) accumulate a frame backlog the browser cannot drain under timer
 * throttling, nor (b) leak a half-dead lease the firmware must time out. The
 * camera keeps streaming to any OTHER viewer (JT/T1078 stream_socket is
 * multi-viewer); only this client disconnects.
 *
 * SSR-safe: returns `true` when `document` is unavailable.
 */
export function useDocumentVisible(): boolean {
  const [visible, setVisible] = useState<boolean>(
    typeof document === 'undefined' ? true : document.visibilityState !== 'hidden',
  );

  useEffect(() => {
    if (typeof document === 'undefined') return undefined;
    const handle = (): void => setVisible(document.visibilityState !== 'hidden');
    document.addEventListener('visibilitychange', handle);
    return () => document.removeEventListener('visibilitychange', handle);
  }, []);

  return visible;
}
